export interface GoogleCloudRestConfig {
  apiKey: string;
  projectId: string;
  languageCode?: string;
  enableInterimResults?: boolean;
  enableSpeakerDiarization?: boolean;
  maxSpeakers?: number;
  model?: string;
  sampleRate?: number;
  audioChannels?: number;
}

export interface RestTranscriptionResponse {
  results: Array<{
    alternatives: Array<{
      transcript: string;
      confidence: number;
    }>;
    isFinal: boolean;
    resultEndTime?: string;
    channelTag?: number;
    languageCode?: string;
  }>;
  speechEventType?: string;
}

export class GoogleCloudRestService {
  private config: GoogleCloudRestConfig;
  private isActive: boolean = false;
  private audioChunks: Uint8Array[] = [];
  private processingInterval: NodeJS.Timeout | null = null;
  private transcriptionCallback: ((result: any) => void) | null = null;

  constructor(config: GoogleCloudRestConfig) {
    this.config = {
      languageCode: 'en-US',
      enableInterimResults: true,
      enableSpeakerDiarization: true,
      maxSpeakers: 2,
      model: 'phone_call',
      sampleRate: 16000,
      audioChannels: 1,
      ...config
    };
  }

  /**
   * Check if REST service is supported
   */
  public static isSupported(): boolean {
    return typeof fetch !== 'undefined';
  }

  /**
   * Start transcription using REST API
   */
  public startTranscription(callback: (result: any) => void): boolean {
    try {
      if (!this.config.apiKey) {
        console.error("❌ API key is required");
        return false;
      }

      if (!this.config.projectId) {
        console.error("❌ Project ID is required");
        return false;
      }

      this.transcriptionCallback = callback;
      this.isActive = true;
      this.audioChunks = [];

      // Start processing audio chunks every 2 seconds
      this.processingInterval = setInterval(() => {
        this.processAudioChunks();
      }, 2000);

      return true;

    } catch (error) {
      console.error("❌ REST service failed:", error);
      return false;
    }
  }

  /**
   * Stop transcription
   */
  public stopTranscription(): void {
    this.isActive = false;
    
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }

    this.transcriptionCallback = null;
    this.audioChunks = [];
  }

  /**
   * Add audio data for processing
   */
  public addAudioData(audioData: Uint8Array): void {
    if (!this.isActive) {
      return;
    }

    this.audioChunks.push(audioData);
  }

  /**
   * Process accumulated audio chunks
   */
  private async processAudioChunks(): Promise<void> {
    if (this.audioChunks.length === 0) {
      return;
    }

    try {
      // Combine all audio chunks
      const totalLength = this.audioChunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combinedAudio = new Uint8Array(totalLength);
      
      let offset = 0;
      for (const chunk of this.audioChunks) {
        combinedAudio.set(chunk, offset);
        offset += chunk.length;
      }

      // Send to Google Cloud Speech API
      const result = await this.sendToGoogleCloud(combinedAudio);
      
      if (result && this.transcriptionCallback) {
        this.transcriptionCallback(result);
      }

      // Clear processed chunks
      this.audioChunks = [];

    } catch (error) {
      // Silent error handling for production
    }
  }

  /**
   * Send audio to Google Cloud Speech API
   */
  private async sendToGoogleCloud(audioData: Uint8Array): Promise<any> {
    try {
      const url = `https://speech.googleapis.com/v1/speech:recognize?key=${this.config.apiKey}`;
      
      const requestBody = {
        config: {
          encoding: 'LINEAR16',
          sampleRateHertz: this.config.sampleRate,
          languageCode: this.config.languageCode,
          enableAutomaticPunctuation: true,
          model: this.config.model,
          useEnhanced: true
        },
        audio: {
          content: this.arrayBufferToBase64(audioData.buffer)
        }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Google Cloud API error:", response.status, errorText);
        throw new Error(`API request failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // Transform the response to match our expected format
      return this.transformResponse(result);

    } catch (error) {
      console.error("❌ Failed to send audio to Google Cloud:", error);
      throw error;
    }
  }

  /**
   * Transform Google Cloud response to our format
   */
  private transformResponse(apiResponse: RestTranscriptionResponse): any {
    if (!apiResponse.results || apiResponse.results.length === 0) {
      return null;
    }

    const result = apiResponse.results[0];
    const alternative = result.alternatives[0];

    return {
      text: alternative.transcript,
      isFinal: result.isFinal,
      confidence: alternative.confidence,
      audioSource: 'mixed' as const,
      startTime: result.resultEndTime,
      endTime: result.resultEndTime
    };
  }

  /**
   * Convert ArrayBuffer to Base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Check if service is active
   */
  public isTranscriptionActive(): boolean {
    return this.isActive;
  }

  /**
   * Dispose of resources
   */
  public dispose(): void {
    this.stopTranscription();
  }
}
